import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useConsent } from "@/contexts/ConsentContext";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { user, subscription } = useAuth();
  const hasActiveSub = subscription.subscribed;
  const { reopenBanner } = useConsent();

  const footerLinks = {
    produkt: [
      { label: "Features", href: "/features" },
      { label: "Preise", href: "/preise" },
      { label: "Konfigurator", href: "/konfigurator" },
      { label: "Branchen", href: "/branchen" },
    ],
    hilfe: [
      { label: "Alle Hilfe-Artikel", href: "/hilfe" },
      { label: "Installation & Setup", href: "/hilfe/installation" },
      { label: "Ersteinrichtung", href: "/hilfe/firstuser" },
      { label: "Stammdaten verwalten", href: "/hilfe/stammdaten" },
      { label: "Hauptmenü anpassen", href: "/hilfe/hauptmenue" },
      { label: "Passwort ändern", href: "/hilfe/passwort-aendern" },
    ],
    unternehmen: [
      { label: "Über uns", href: "/ueber-uns" },
    ],
    rechtliches: [
      { label: "Impressum", href: "/impressum" },
      { label: "Datenschutz", href: "/datenschutz" },
      { label: "AGB", href: "/agb" },
      { label: "Cookie-Einstellungen", href: "#cookies" },
    ],
  };

  return (
    <footer className="relative overflow-hidden border-t border-border bg-secondary/30">
      {/* CTA Section */}
      <div className="container py-16 lg:py-20">
        <div className="relative overflow-hidden rounded-3xl bg-primary p-8 lg:p-12">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary-foreground/5 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-accent/20 blur-3xl" />

          <div className="relative flex flex-col items-center gap-6 text-center lg:flex-row lg:justify-between lg:text-left">
            <div className="max-w-xl">
              <h3 className="mb-2 font-display text-2xl font-bold text-primary-foreground lg:text-3xl">
                Bereit für Ihr eigenes Modul-Setup?
              </h3>
              <p className="text-primary-foreground/70">
                {hasActiveSub
                  ? "Passen Sie Ihre Module an und optimieren Sie Ihr Setup."
                  : "Klicken Sie sich Ihr System zusammen – und starten Sie mit 1 Monat kostenlosem Zugang."}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
              <Button
                size="lg"
                className="group h-14 bg-accent px-8 text-base font-semibold text-accent-foreground shadow-lg shadow-accent/25 transition-all hover:shadow-xl hover:shadow-accent/30"
                asChild
              >
                <Link to="/konfigurator">
                  Konfigurator öffnen
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button
                variant="ghost"
                size="lg"
                className="h-14 px-6 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
                asChild
              >
                <Link to="/preise">Preise ansehen</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container pb-12">
        <div className="grid gap-12 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <Link to="/" className="mb-6 inline-flex items-center">
              <img src="/favicon.svg" alt="NextInvoice" className="h-10" />
            </Link>
            <p className="mb-6 max-w-sm text-muted-foreground">
              Der modulare Baukasten für Ihre Geschäftsprozesse.
              Für Selbstständige und Unternehmen, die mehr erwarten.
            </p>

            <div className="space-y-3">
              <a href="mailto:info@nxtinvoice.de" className="flex items-center gap-3 text-sm text-muted-foreground transition-colors hover:text-foreground">
                <Mail className="h-4 w-4" />
                info@nxtinvoice.de
              </a>
              <a href="tel:+4915565350880" className="flex items-center gap-3 text-sm text-muted-foreground transition-colors hover:text-foreground">
                <Phone className="h-4 w-4" />
                +49 15565 350880
              </a>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" />
                Neugersdorf, Deutschland
              </div>
            </div>
          </div>

          <div>
            <h4 className="mb-5 font-display text-sm font-bold uppercase tracking-wider text-foreground">Produkt</h4>
            <ul className="space-y-3">
              {footerLinks.produkt.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-sm text-muted-foreground transition-colors hover:text-foreground">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-5 font-display text-sm font-bold uppercase tracking-wider text-foreground">Hilfe</h4>
            <ul className="space-y-3">
              {footerLinks.hilfe.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-sm text-muted-foreground transition-colors hover:text-foreground">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-5 font-display text-sm font-bold uppercase tracking-wider text-foreground">Unternehmen</h4>
            <ul className="space-y-3">
              {footerLinks.unternehmen.map((link) => (
                <li key={link.label}>
                  {link.href.startsWith("/") ? (
                    <Link to={link.href} className="text-sm text-muted-foreground transition-colors hover:text-foreground">{link.label}</Link>
                  ) : (
                    <a href={link.href} className="text-sm text-muted-foreground transition-colors hover:text-foreground">{link.label}</a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-5 font-display text-sm font-bold uppercase tracking-wider text-foreground">Rechtliches</h4>
            <ul className="space-y-3">
              {footerLinks.rechtliches.map((link) => (
                <li key={link.label}>
                  {link.href === "#cookies" ? (
                    <button onClick={reopenBanner} className="text-sm text-muted-foreground transition-colors hover:text-foreground">
                      {link.label}
                    </button>
                  ) : (
                    <Link to={link.href} className="text-sm text-muted-foreground transition-colors hover:text-foreground">{link.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-border pt-8 sm:flex-row">
          <p className="text-sm text-muted-foreground">
            © {currentYear} Lizenzwerk24. Alle Rechte vorbehalten.
          </p>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-success" />
              <span className="text-xs font-medium text-muted-foreground">Datenschutz nach DSGVO</span>
            </div>
            <span className="text-xs text-muted-foreground">•</span>
            <span className="text-xs text-muted-foreground">Deutsche Server</span>
            <span className="text-xs text-muted-foreground">•</span>
            <span className="text-xs text-muted-foreground">SSL-verschlüsselt</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
